import { createContext, useContext, useMemo } from 'react';
import { useAuth } from '../auth';
import { apiRequest } from '../fetchUtils';
import { toFormData } from 'axios';
import {
  GlobalPromoMediaType,
  MarketplacePayout,
  MarketplaceRole,
  Attribute,
  RoleAccount,
  Listing,
  Order,
  MarketplaceEarning,
  SocialConnectedProfile,
  BasicCategory,
  DomainInfo,
  GlobalRole,
  Lead,
  MarketplaceInfo,
  PromoAutomation,
  ThirdPartyQuery,
  CategoryGeneratorBody,
  CategoryGeneratorResponse,
  CategoryRolePermission,
  Bid,
  MaxBid,
  MarketplaceTheme,
  ThemeOptions,
  AttributeOption,
  TemplateNamespaceMapping,
  TaskLog,
  DigestEmailType,
  DigestCategoryEmailTemplate,
  EmailPreference,
  MarketplacePayoutConfig,
  MarketplacePage,
  MarketplaceLinkInBioItem,
  MarketplaceUserAccount,
  ListingAnalytics,
  MonetizationOption,
  SocialProfile,
  SearchPrompt,
  Category,
  Conversation,
  Message,
  PaginatedMessageResponse,
  OnboardingChecklist,
  MarketplaceAdminResponse,
} from 'src/api/v1-api';
import { StripeConnectComponents } from 'src/components/organisms/stripe-connect-form/stripe-connect-onboarding';
import { StripeAccount } from 'src/components/organisms/stripe-connect-form';
import { PreviewPromoMediaData } from 'src/components/organisms/promo-automation-form/PromoListingForm';

export type ApiRequestContextParams = {
  /**
   * Base URL of the endpoint. Could contain final slashes
   */
  baseUrl?: string;
};

const ApiRequestContext = createContext<ApiRequestContextParams>({
  baseUrl: '',
});

export const ApiRequestProvider = ApiRequestContext.Provider;

const useApiRequestConfig = () => {
  return useContext(ApiRequestContext);
};

export type RequestUrlParams = {
  /**
   * Relative URL for the endpoint
   */
  url: string;

  /**
   * Provide a body that will be serialized and sent to the endpoint in JSON format
   */
  body?: unknown;

  /**
   * Provide a pre-formatted body that will be sent to the endpoint in JSON format
   */
  formData?: FormData;

  /**
   * Indicates whether the body contains images and should be treated as form data instead
   */
  hasFiles?: boolean;

  /**
   * Use the access token for this request.
   */
  useToken?: boolean;

  /**
   * Indicates if the token should be refreshed
   */
  shouldRefreshToken?: boolean;

  /**
   * URL parameters to be appended to the URL
   */
  urlParams?: unknown;
};

export type MethodRequestUrlParams = RequestUrlParams & {
  method: string;
};

const useRequest = () => {
  const { getToken, refreshToken, isAuthenticated } = useAuth();
  const { baseUrl } = useApiRequestConfig();

  const performTokenedRequest = useMemo(() => {
    const makeRequest = async <T>(
      params: MethodRequestUrlParams,
      isRetry = false,
      providedToken?: string,
    ): Promise<T> => {
      const { method, url, body, formData, hasFiles } = params;

      // Use provided token (from retry) or fetch a fresh one
      let token: string;
      if (providedToken) {
        token = providedToken;
      } else {
        // dont throw ASAP if isAuthenticated is false
        // isAuthenticated may be out of sync with the actual auth state, try to get a token in this case (it will refresh if needed)
        try {
          token = await getToken();
        } catch (error) {
          const authError = new Error('Authentication failed. Please log in again.');
          (authError as any).isAuthError = true;
          throw authError;
        }

        // If getToken returns an empty string, treat it as an authentication failure
        if (!token) {
          const authError = new Error('Authentication failed. Please log in again.');
          (authError as any).isAuthError = true;
          (authError as any).status = 401;
          throw authError;
        }
      }

      const completeUrl = `${baseUrl}${url}`;

      let parsedBody = body ?? formData;

      if (body && hasFiles) {
        parsedBody = toFormData(body as any);
      }
      const treatAsFormData = hasFiles || !!formData;

      try {
        return (await apiRequest(method, completeUrl, token, parsedBody, treatAsFormData)) as T;
      } catch (error: any) {
        // If we get a 403 and haven't already retried, refresh token and try once more
        if (error?.status === 403 && !isRetry) {
          try {
            // Force a token refresh and capture the new token
            const newToken = await refreshToken();
            // Retry the request once with the new token directly
            return await makeRequest<T>(params, true, newToken);
          } catch (retryError) {
            throw retryError;
          }
        }
        throw error;
      }
    };

    return makeRequest;
  }, [getToken, baseUrl, refreshToken]);

  const performUserAccessTokenRequest = useMemo(
    () =>
      async <T>(params: MethodRequestUrlParams, urlParams: any, token: string) => {
        const { method, url, body, formData, hasFiles } = params;
        const completeUrl = `${baseUrl}${url}`;
        let parsedBody = body ?? formData;
        if (body && hasFiles) {
          parsedBody = toFormData(body as any);
        }
        const treatAsFormData = hasFiles || !!formData;
        return (await apiRequest(
          method,
          completeUrl,
          null,
          parsedBody,
          treatAsFormData,
          urlParams,
          token,
        )) as T;
      },
    [baseUrl],
  );

  const performUnauthRequest = useMemo(
    () =>
      async <T>(params: MethodRequestUrlParams) => {
        const { method, url, body, formData, hasFiles } = params;
        const completeUrl = `${baseUrl}${url}`;

        let parsedBody = body ?? formData;

        if (body && hasFiles) {
          parsedBody = toFormData(body as any);
        }
        const treatAsFormData = hasFiles || !!formData;

        // Create a new Headers object with CORS-specific headers
        const headers = new Headers();
        headers.append('Accept', 'application/json');
        if (!treatAsFormData) {
          headers.append('Content-Type', 'application/json');
        }

        // Use fetch directly instead of apiRequest
        const response = await fetch(completeUrl, {
          method,
          headers,
          mode: 'cors',
          credentials: 'include',
          body: treatAsFormData
            ? (parsedBody as FormData)
            : parsedBody
            ? JSON.stringify(parsedBody)
            : undefined,
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Handle both JSON and text responses
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          return (await response.json()) as T;
        }
        return (await response.text()) as T;
      },
    [baseUrl],
  );

  const post = useMemo(
    () =>
      <T>(params: RequestUrlParams) =>
        performTokenedRequest<T>({ method: 'POST', ...params }),
    [performTokenedRequest],
  );

  const remove = useMemo(
    () =>
      <T>(params: RequestUrlParams) =>
        performTokenedRequest<T>({ method: 'DELETE', ...params }),
    [performTokenedRequest],
  );

  const put = useMemo(
    () =>
      <T>(params: RequestUrlParams) =>
        performTokenedRequest<T>({ method: 'PUT', ...params }),
    [performTokenedRequest],
  );

  const putWithUserAccessToken = useMemo(
    () =>
      <T>(params: RequestUrlParams, urlParams: any, token: string) =>
        performUserAccessTokenRequest<T>({ method: 'PUT', ...params }, urlParams, token),
    [performUserAccessTokenRequest],
  );

  const deleteRequest = useMemo(
    () =>
      <T>(params: RequestUrlParams) =>
        performTokenedRequest<T>({ method: 'DELETE', ...params }),
    [performTokenedRequest],
  );

  const get = useMemo(() => {
    const makeGetRequest = async <T>(
      params: RequestUrlParams,
      isRetry = false,
      providedToken?: string,
    ): Promise<T> => {
      const { url, body, useToken, shouldRefreshToken, urlParams } = params;
      if (useToken && !isAuthenticated) {
        throw new Error('User is not authenticated');
      }

      const completeUrl = `${baseUrl}${url}`;
      let token: string | null = null;

      if (providedToken) {
        // Use the provided token from retry
        token = providedToken;
      } else if (shouldRefreshToken) {
        token = await refreshToken();
      } else if (useToken) {
        token = await getToken();
      }

      try {
        return (await apiRequest('GET', completeUrl, token, body, false, urlParams)) as T;
      } catch (error: any) {
        // If we get a 403 and haven't already retried, refresh token and try once more
        if (error?.status === 403 && useToken && !isRetry) {
          try {
            // Force a token refresh and capture the new token
            const newToken = await refreshToken();
            // Retry the request once with the new token directly
            return await makeGetRequest<T>(params, true, newToken);
          } catch (retryError) {
            throw retryError;
          }
        }
        throw error;
      }
    };

    return makeGetRequest;
  }, [isAuthenticated, baseUrl, refreshToken, getToken]);

  const api = useMemo(
    () => ({
      shareautomation: {
        listings: {
          sharePromoMedia: async (marketplaceId: number, listingId: number, body?: object) =>
            post({
              url: `/shareautomation/${marketplaceId}/listings/${listingId}/promo_media/share`,
              useToken: true,
              body,
            }),
        },
        connectedSocialProfiles: {
          manageConnectionsUrl: async (marketplaceId: number) =>
            get<{ url: string }>({
              url: `/marketplaces/${marketplaceId}/social_connector_profile/manage_connections_url`,
              useToken: true,
            }),
          list: async (marketplaceId: number) =>
            get<{ connected_accounts: SocialConnectedProfile[] }>({
              url: `/marketplaces/${marketplaceId}/social_connector_profile/connected_accounts`,
              useToken: true,
            }),
        },
        globalPromoMediaTypes: {
          list: async (marketplaceId: number) =>
            get<GlobalPromoMediaType[]>({
              url: `/shareautomation/${marketplaceId}/global_promo_media_types/`,
              useToken: true,
            }),
          namespaceMapping: async (marketplaceId: number) =>
            get<TemplateNamespaceMapping>({
              url: `/shareautomation/${marketplaceId}/global_promo_media_types/namespace_mapping/`,
              useToken: true,
            }),
        },
        promoAutomations: {
          getByCategoryId: async (marketplaceId: number, categoryId: number) =>
            get<PromoAutomation[]>({
              url: `/shareautomation/marketplace/${marketplaceId}/category/${categoryId}/promo_automation/`,
              useToken: true,
            }),
          getByMarketplaceId: async (marketplaceId: number) =>
            get<PromoAutomation[]>({
              url: `/shareautomation/marketplace/${marketplaceId}/promo_automation/`,
              useToken: true,
            }),
          create: async (marketplaceId: number, categoryId: number, body: object) =>
            post<PromoAutomation>({
              url: `/shareautomation/marketplace/${marketplaceId}/category/${categoryId}/promo_automation/`,
              useToken: true,
              body,
            }),
          update: async (
            marketplaceId: number,
            categoryId: number,
            promoAutomationId: number,
            body: Partial<PromoAutomation>,
          ) =>
            put<PromoAutomation>({
              url: `/shareautomation/marketplace/${marketplaceId}/category/${categoryId}/promo_automation/${promoAutomationId}/`,
              useToken: true,
              body,
            }),
          delete: async (marketplaceId: number, categoryId: number, promoAutomationId: number) =>
            deleteRequest<PromoAutomation>({
              url: `/shareautomation/marketplace/${marketplaceId}/category/${categoryId}/promo_automation/${promoAutomationId}/`,
              useToken: true,
            }),
          createPreview: async (marketplaceId: number, categoryId: number, body: object) =>
            post<{ image_url: string; caption: string }>({
              url: `/shareautomation/marketplace/${marketplaceId}/category/${categoryId}/promo_automation/preview/`,
              useToken: true,
              body,
            }),
          createSinglePreview: async (marketplaceId: number, listingId: number, body: object) =>
            post<PreviewPromoMediaData>({
              url: `/shareautomation/marketplace/${marketplaceId}/listing/${listingId}/promo_media/preview/`,
              useToken: true,
              body,
            }),
          shareSingle: async (marketplaceId: number, listingId: number, body?: object) =>
            post({
              url: `/shareautomation/marketplace/${marketplaceId}/listing/${listingId}/promo_media/share`,
              useToken: true,
              body,
            }),
          duplicate: async (
            marketplaceId: number,
            categoryId: number,
            promoAutomationId: number,
            body: object,
          ) =>
            post<PromoAutomation>({
              url: `/shareautomation/marketplace/${marketplaceId}/category/${categoryId}/promo_automation/${promoAutomationId}/duplicate/`,
              useToken: true,
              body,
            }),
          getDefaultTemplateValues: async (marketplaceId: number, globalPromoMediaTypeId: number) =>
            get<any>({
              url: `/shareautomation/marketplace/${marketplaceId}/${globalPromoMediaTypeId}/default_template_values/`,
              useToken: true,
            }),
        },
      },
      marketplace: {
        create: {
          create: async (body?: object) =>
            post<MarketplaceInfo>({
              url: `/marketplaces/create/`,
              useToken: true,
              body,
            }),
        },
        update: {
          put: async (marketplaceId: number, formData: FormData, hasFiles = false) =>
            put<MarketplaceInfo>({
              url: `/marketplaces/${marketplaceId}/`,
              useToken: true,
              formData,
              hasFiles,
            }),
        },
        info: {
          get: async (marketplaceId: number) =>
            get<MarketplaceInfo>({
              url: `/marketplaces/${marketplaceId}/info/`,
              useToken: true,
            }),
        },
        hostname: {
          get: async (hostname: string) =>
            get<MarketplaceInfo>({
              url: `/marketplaces/${hostname}/hostname/`,
            }),
        },
        roles: {
          list: async (marketplaceId: number) =>
            get<MarketplaceRole[]>({
              url: `/marketplaces/${marketplaceId}/roles/`,
              useToken: true,
            }),
          get: async (marketplaceRoleId: number | string) =>
            get<MarketplaceRole>({
              url: `/marketplaces/roles/${marketplaceRoleId}/`,
              useToken: true,
            }),
          attributes: {
            list: async (marketplaceId: number, marketplaceRoleId: number) =>
              get<Attribute[]>({
                url: `/marketplaces/${marketplaceId}/roles/${marketplaceRoleId}/attributes/`,
                useToken: true,
              }),
          },
          create: async (marketplaceId: number, body: object) =>
            post<MarketplaceRole>({
              url: `/marketplaces/${marketplaceId}/roles/`,
              useToken: true,
              body,
            }),
        },
        earnings: {
          list: async (marketplaceId: number, params?: any) =>
            get<{
              results: MarketplaceEarning[];
              previous: string;
              next: string;
              count: number;
              page_size: number;
              page_number: string;
            }>({
              url: `/marketplaces/${marketplaceId}/earnings/`,
              useToken: true,
              urlParams: params,
            }),
        },
        payouts: {
          list: async (marketplaceId: number, params?: { paid: boolean }) =>
            get<MarketplacePayout[]>({
              url: `/marketplaces/${marketplaceId}/payouts/`,
              useToken: true,
              urlParams: params,
            }),
          create: async (marketplaceId: number, body: object) =>
            post<MarketplacePayout>({
              url: `/marketplaces/${marketplaceId}/payouts/`,
              useToken: true,
              body,
            }),
          balance: async (marketplaceId: number) =>
            get<{ payouts: number; earnings: number; balance: number }>({
              url: `/marketplaces/${marketplaceId}/payout_balance/`,
              useToken: true,
            }),
        },
        payoutConfig: {
          get: async (marketplaceId: number) =>
            get<MarketplacePayoutConfig>({
              url: `/marketplaces/${marketplaceId}/payout_config/`,
              useToken: true,
            }),
          update: async (marketplaceId: number, body: Partial<MarketplacePayoutConfig>) =>
            put<MarketplacePayoutConfig>({
              url: `/marketplaces/${marketplaceId}/payout_config/`,
              useToken: true,
              body,
            }),
        },
        query: {
          list: (marketplaceId: number) =>
            get<ThirdPartyQuery[]>({
              url: `/marketplaces/${marketplaceId}/queries/`,
              useToken: true,
            }),
          get: async (marketplaceId: number, queryId: string) =>
            get<ThirdPartyQuery>({
              url: `/marketplaces/${marketplaceId}/queries/${queryId}/`,
              useToken: true,
            }),
          create: async (marketplaceId: number, body: object) =>
            post<ThirdPartyQuery>({
              url: `/marketplaces/${marketplaceId}/queries/`,
              useToken: true,
              body,
            }),
          update: async (marketplaceId: number, queryId: number, body: object) =>
            put<ThirdPartyQuery>({
              url: `/marketplaces/${marketplaceId}/queries/${queryId}/`,
              useToken: true,
              body,
            }),
          logs: {
            list: async (marketplaceId: number, queryId: string) =>
              get<{
                results: TaskLog[];
                previous: string | null;
                next: string | null;
                count: number;
                page_size: number;
                page_number: number;
              }>({
                url: `/marketplaces/${marketplaceId}/queries/${queryId}/logs/`,
                useToken: true,
              }),
          },
        },
        user: {
          list: async (marketplaceId: number, params?: any) =>
            get<{
              results: MarketplaceUserAccount[];
              previous: string;
              next: string;
              count: number;
              page_size: number;
              page_number: string;
            }>({
              url: `/marketplaces/${marketplaceId}/users/`,
              useToken: true,
              urlParams: params,
            }),
          updateMarketplaceAccountStatus: async (
            marketplaceId: number,
            marketplaceAccountId: number,
            payload: { status: string; reason: string },
          ) =>
            put<MarketplaceUserAccount>({
              url: `/marketplaces/${marketplaceId}/users/${marketplaceAccountId}/status/`,
              useToken: true,
              body: payload,
            }),
          all: async (marketplaceId: number) =>
            get<MarketplaceUserAccount[]>({
              url: `/marketplaces/${marketplaceId}/users/all`,
              useToken: true,
            }),
          role: {
            get: async (userId: string, roleId: number) =>
              get<RoleAccount>({
                url: `/users/${userId}/role/${roleId}`,
                useToken: true,
              }),
          },
          roles: {
            list: async (marketplaceId: number) =>
              get<RoleAccount[]>({
                url: `/marketplaces/${marketplaceId}/user/roles/`,
                useToken: true,
              }),
            add: async (userId: string, body?: object) =>
              post<RoleAccount>({
                url: `/users/${userId}/roles/`,
                useToken: true,
                body,
              }),
            remove: async (userId: string, roleId: number, body?: object) =>
              remove({
                url: `/users/${userId}/roles/${roleId}`,
                useToken: true,
                body,
              }),
            cancelSubscription: async (userId: string, roleId: number) =>
              post<RoleAccount>({
                url: `/users/${userId}/roles/${roleId}/cancel_subscription/`,
                useToken: true,
              }),
            global: {
              list: async () =>
                get<GlobalRole[]>({
                  url: `/roles/global/`,
                  useToken: true,
                }),
            },
          },
        },
        listings: {
          get: (listingId: number) =>
            get<Listing>({
              url: `/listings/${listingId}`,
              useToken: true,
            }),
          cancelSubscription: async (listingId: number, marketplaceId: number) =>
            post<Listing>({
              url: `/listings/${listingId}/${marketplaceId}/cancel_subscription/`,
              useToken: true,
            }),
          setStatusAndSubsStatus: async (
            listingId: number,
            marketplaceId: number,
            status: string,
            subStatus: string,
            userAccessToken: string,
          ) =>
            putWithUserAccessToken<Listing>(
              {
                url: `/listings/${listingId}/${marketplaceId}/set_status_and_sub_status/${status}/${subStatus}/`,
              },
              null,
              userAccessToken,
            ),
          setSoldPrice: async (
            listingId: number,
            marketplaceId: number,
            body: { sold_price: string | number },
            userAccessToken: string,
          ) =>
            putWithUserAccessToken<Listing>(
              {
                url: `/listings/${listingId}/${marketplaceId}/set_sold_price/`,
                body,
              },
              null,
              userAccessToken,
            ),
          bulkImport: async (marketplaceId: number, body: FormData) =>
            post<Listing[]>({
              url: `/marketplaces/${marketplaceId}/listings/bulk_import/`,
              useToken: true,
              formData: body,
            }),
        },
        concierge_listings: {
          get: (marketplaceId: number) =>
            get<Listing[]>({
              url: `/listings/${marketplaceId}/concierge/`,
              useToken: true,
            }),
        },
        leads: {
          create: async (marketplaceId: number, listingId: number, formData: FormData) =>
            post<Lead>({
              url: `/leads/${marketplaceId}/${listingId}/create/`,
              useToken: true,
              formData,
            }),
          followUp: async (marketplaceId: number, leadId: number, body: object) =>
            performUnauthRequest<Lead>({
              method: 'POST',
              url: `/leads/${leadId}/${marketplaceId}/follow_up/`,
              useToken: false,
              body,
            }),
        },
        orders: {
          get: (marketplaceId: number, orderId: number) =>
            get<Order>({
              url: `/orders/${marketplaceId}/${orderId}/`,
              useToken: true,
            }),
        },
        categories: {
          getDetails: async (marketplaceId: number, categoryId: number) =>
            get<Category>({
              url: `/marketplaces/${marketplaceId}/categories/${categoryId}/`,
              useToken: false,
            }),
          list: async (marketplaceId: number) =>
            get<BasicCategory[]>({
              url: `/marketplaces/${marketplaceId}/all_categories/`,
              useToken: true,
            }),
          createCheckoutSession: async (
            categoryId: number,
            body: { return_url: string; client_reference_id: string },
          ) =>
            post<string>({
              url: `/categories/${categoryId}/checkout_session/`,
              useToken: true,
              body,
            }),
          permissions: {
            get: async (categoryId: number) =>
              get<CategoryRolePermission[]>({
                url: `/marketplaces/categories/${categoryId}/`,
                useToken: true,
              }),
          },
          parents: {
            list: async (categoryId: number) =>
              get<BasicCategory[]>({
                url: `/categories/${categoryId}/parent_list/`,
                useToken: true,
              }),
          },
          generate: async (body: CategoryGeneratorBody) =>
            put<string>({
              url: `/marketplaces/category/json/generator/`,
              useToken: true,
              body,
            }),
          import: async (body: CategoryGeneratorBody) =>
            put<CategoryGeneratorResponse>({
              url: `/marketplaces/category/json/import/`,
              useToken: true,
              body,
            }),
          update: async (marketplaceId: number, categoryId: number, body: Partial<BasicCategory>) =>
            put<BasicCategory>({
              url: `/categories/${marketplaceId}/${categoryId}/`,
              useToken: true,
              body,
            }),
          updateFromQuickLaunch: async (marketplaceId: number, categoryId: number, body: object) =>
            post<BasicCategory>({
              url: `/marketplaces/${marketplaceId}/categories/${categoryId}/quick_launch/`,
              useToken: true,
              body,
            }),
          createFromQuickLaunch: async (marketplaceID: number, body: object) =>
            post<BasicCategory>({
              url: `/marketplaces/${marketplaceID}/categories/quick_launch/`,
              useToken: true,
              body,
            }),
        },
        check_dns: {
          get: (marketplaceId: number) =>
            get<DomainInfo>({
              url: `/marketplaces/${marketplaceId}/check_dns/`,
              useToken: true,
            }),
        },
        check_email_dns: {
          get: (marketplaceId: number, email_or_digest: 'email' | 'digest') =>
            get<DomainInfo>({
              url: `/marketplaces/${marketplaceId}/check_email_dns/${email_or_digest}/`,
              useToken: true,
            }),
        },
        generate_apex_email_dns: async (
          marketplaceId: number,
          body: { email_domain: string; email: string; email_or_digest: 'email' | 'digest' },
        ) =>
          post<DomainInfo>({
            url: `/marketplaces/${marketplaceId}/generate_apex_email_dns/`,
            useToken: true,
            body,
          }),
        domain: {
          get: (marketplaceId: number) =>
            get<DomainInfo>({
              url: `/marketplaces/${marketplaceId}/domain/`,
            }),
        },
        auctions: {
          bids: {
            create: async (auctionId: number, body: { bid_amount: string }) =>
              post<MaxBid>({
                url: `/auctions/${auctionId}/bid/`,
                useToken: true,
                body,
              }),
            list: async (auctionId: number) =>
              get<Bid[]>({
                url: `/auctions/${auctionId}/bids/`,
                useToken: true,
              }),
          },
          anon_max_bid: {
            get: async (auctionId: number) =>
              get<MaxBid>({
                url: `/auctions/${auctionId}/max_bid/`,
              }),
          },
          max_bid: {
            get: async (auctionId: number) =>
              get<MaxBid>({
                url: `/auctions/${auctionId}/max_bid/`,
                useToken: true,
              }),
          },
          relist: {
            post: async (auctionId: number) =>
              post({
                url: `/auctions/${auctionId}/relist-auction/`,
                useToken: true,
              }),
          },
        },
        theme: {
          options: {
            get: async (marketplaceId: number) =>
              get<{ marketplaceTheme: MarketplaceTheme; options: ThemeOptions }>({
                url: `/marketplaces/${marketplaceId}/theme/`,
              }),
          },
        },
        digestCategoryEmailTemplates: {
          getByCategoryId: async (marketplaceId: number, categoryId: number) =>
            get<DigestCategoryEmailTemplate[]>({
              url: `/marketplaces/${marketplaceId}/category/${categoryId}/digest_category_email_templates/`,
              useToken: true,
            }),
          getByMarketplaceId: async (marketplaceId: number) =>
            get<DigestCategoryEmailTemplate[]>({
              url: `/marketplaces/${marketplaceId}/digest_category_email_templates/`,
              useToken: true,
            }),
          update: async (marketplaceId: number, categoryId: number, id: number, payload: object) =>
            put<DigestCategoryEmailTemplate>({
              url: `/marketplaces/${marketplaceId}/category/${categoryId}/digest_category_email_templates/${id}/`,
              useToken: true,
              body: payload,
            }),
          create: async (marketplaceId: number, categoryId: number, payload: object) =>
            post<DigestCategoryEmailTemplate>({
              url: `/marketplaces/${marketplaceId}/category/${categoryId}/digest_category_email_templates/`,
              useToken: true,
              body: payload,
            }),
          delete: async (marketplaceId: number, categoryId: number, id: number) =>
            remove<DigestCategoryEmailTemplate>({
              url: `/marketplaces/${marketplaceId}/category/${categoryId}/digest_category_email_templates/${id}/`,
              useToken: true,
            }),
        },
        emailPreferences: {
          list: async (marketplaceId: number) =>
            get<EmailPreference[]>({
              url: `/marketplaces/${marketplaceId}/email_preferences/`,
              useToken: true,
            }),
          manageSubscription: async (
            marketplaceId: number,
            emailPreferenceId: number,
            body: { subscribe: boolean },
          ) =>
            put<EmailPreference>({
              url: `/marketplaces/${marketplaceId}/email_preferences/${emailPreferenceId}/`,
              useToken: true,
              body,
            }),
          manageAllSubscriptions: async (marketplaceId: number, payload: { subscribe: boolean }) =>
            put<EmailPreference[]>({
              url: `/marketplaces/${marketplaceId}/email_preferences/manage_subscriptions/`,
              useToken: true,
              body: payload,
            }),
          manageSubscriptionWithToken: async (
            marketplaceId: number,
            emailPreferenceId: number,
            body: { subscribe: boolean },
            userAccessToken: string,
          ) =>
            putWithUserAccessToken<EmailPreference>(
              {
                url: `/marketplaces/${marketplaceId}/email_preferences/${emailPreferenceId}/`,
                body,
              },
              null,
              userAccessToken,
            ),
          manageAllSubscriptionsWithToken: async (
            marketplaceId: number,
            payload: { subscribe: boolean },
            userAccessToken: string,
          ) =>
            putWithUserAccessToken<EmailPreference[]>(
              {
                url: `/marketplaces/${marketplaceId}/email_preferences/manage_subscriptions/`,
                body: payload,
              },
              null,
              userAccessToken,
            ),
        },
        pages: {
          list: async (marketplaceId: number) =>
            get<MarketplacePage[]>({
              url: `/marketplaces/${marketplaceId}/pages/`,
              useToken: true,
            }),
          delete: async (marketplaceId: number, pageId: number) =>
            remove<MarketplacePage>({
              url: `/marketplaces/${marketplaceId}/pages/${pageId}/`,
              useToken: true,
            }),
          update: async (marketplaceId: number, pageId: number, body: Partial<MarketplacePage>) =>
            put<MarketplacePage>({
              url: `/marketplaces/${marketplaceId}/pages/${pageId}/`,
              useToken: true,
              body,
            }),
          create: async (marketplaceId: number, body: Partial<MarketplacePage>) =>
            post<MarketplacePage>({
              url: `/marketplaces/${marketplaceId}/pages/`,
              useToken: true,
              body,
            }),
        },
        linkInBioItems: {
          list: async (marketplaceId: number) =>
            get<MarketplaceLinkInBioItem[]>({
              url: `/marketplaces/${marketplaceId}/link_in_bio_items/`,
              useToken: true,
            }),
          update: async (marketplaceId: number, itemId: number, body: object) =>
            put<MarketplaceLinkInBioItem>({
              url: `/marketplaces/${marketplaceId}/link_in_bio_items/${itemId}/`,
              useToken: true,
              body,
            }),
          create: async (marketplaceId: number, body: object) =>
            post<MarketplaceLinkInBioItem>({
              url: `/marketplaces/${marketplaceId}/link_in_bio_items/`,
              useToken: true,
              body,
            }),
          delete: async (marketplaceId: number, itemId: number) =>
            remove<MarketplaceLinkInBioItem>({
              url: `/marketplaces/${marketplaceId}/link_in_bio_items/${itemId}/`,
              useToken: true,
            }),
          updatePositions: async (marketplaceId: number, body: object) =>
            put<MarketplaceLinkInBioItem[]>({
              url: `/marketplaces/${marketplaceId}/link_in_bio_items/update_positions/`,
              useToken: true,
              body,
            }),
        },
        analytics: async (marketplaceId: number) =>
          get<ListingAnalytics[]>({
            url: `/marketplaces/${marketplaceId}/analytics/`,
            useToken: true,
          }),
        launchPreview: async (marketplaceId: number) =>
          post<void>({
            url: `/marketplaces/${marketplaceId}/launch_preview/`,
            useToken: true,
          }),
        socialProfiles: {
          create: async (marketplaceId: number, body: Partial<SocialProfile>) =>
            post<SocialProfile>({
              url: `/marketplaces/${marketplaceId}/social_profiles/`,
              useToken: true,
              body,
            }),
          list: async (marketplaceId: number) =>
            get<SocialProfile[]>({
              url: `/marketplaces/${marketplaceId}/social_profiles/`,
              useToken: true,
            }),
          update: async (marketplaceId: number, profileId: number, body: Partial<SocialProfile>) =>
            put<SocialProfile>({
              url: `/marketplaces/${marketplaceId}/social_profiles/${profileId}/`,
              useToken: true,
              body,
            }),
          delete: async (marketplaceId: number, profileId: number) =>
            remove<SocialProfile>({
              url: `/marketplaces/${marketplaceId}/social_profiles/${profileId}/`,
              useToken: true,
            }),
        },
        tier: {
          cancel: async (marketplaceId: number) =>
            post<string>({
              url: `/marketplaces/${marketplaceId}/tier/cancel/`,
              useToken: true,
            }),
          checkout: async (
            body: { tier_id?: number; tier_position?: number; is_trial?: boolean },
            marketplaceId: number,
          ) =>
            post<string>({
              url: `/marketplaces/${marketplaceId}/tier/checkout_session/`,
              useToken: true,
              body,
            }),
          createCheckoutSession: async (
            marketplaceId: number,
            body: { tier_id?: number; tier_position?: number },
          ) =>
            post<string>({
              url: `/marketplaces/${marketplaceId}/tier/checkout_session/`,
              useToken: true,
              body,
            }),
        },
        billing: {
          getPortalUrl: async (marketplaceId: number) =>
            post<{ url: string }>({
              url: `/marketplaces/${marketplaceId}/billing-portal/`,
              useToken: true,
            }),
        },
        trial: {
          getStatus: async (marketplaceId: number) =>
            get<{
              is_trial: boolean;
              has_subscription: boolean;
              tier_name?: string;
              tier_position?: number;
              days_remaining?: number;
              trial_end_date?: string;
            }>({
              url: `/marketplaces/${marketplaceId}/trial-status/`,
              useToken: true,
            }),
        },
        searchPrompts: {
          list: async (marketplaceId: number, params?: any) =>
            get<{
              results: SearchPrompt[];
              previous: string;
              next: string;
              count: number;
              page_size: number;
              page_number: string;
            }>({
              url: `/marketplaces/${marketplaceId}/search_prompts/`,
              useToken: true,
              urlParams: params,
            }),
          create: async (marketplaceId: number, body: object) =>
            post<SearchPrompt>({
              url: `/marketplaces/${marketplaceId}/search_prompts/`,
              useToken: true,
              body,
            }),
          update: async (marketplaceId: number, promptId: number, body: object) =>
            put<SearchPrompt>({
              url: `/marketplaces/${marketplaceId}/search_prompts/${promptId}/`,
              useToken: true,
              body,
            }),
          delete: async (marketplaceId: number, promptId: number) =>
            remove({
              url: `/marketplaces/${marketplaceId}/search_prompts/${promptId}/`,
              useToken: true,
            }),
          run: async (marketplaceId: number, promptId: number) =>
            post({
              url: `/marketplaces/${marketplaceId}/search_prompts/${promptId}/run/`,
              useToken: true,
            }),
        },
        sso: {
          generateTicket: async (marketplaceId: number, redirectUri: string, payload: object) =>
            performUnauthRequest<string>({
              method: 'POST',
              url: `/marketplaces/${marketplaceId}/generate_ticket/`,
              body: { redirect_uri: redirectUri, payload },
            }),
          sendMagicLink: async (
            marketplaceId: number,
            body: { email: string; return_uri?: string },
          ) =>
            performUnauthRequest<{ ok: boolean }>({
              method: 'POST',
              url: `/marketplaces/${marketplaceId}/send_magic_link/`,
              body,
            }),
        },
        messaging: {
          conversation: {
            create: async (marketplaceId: number, body: object) =>
              post<Conversation>({
                url: `/marketplaces/${marketplaceId}/conversations/`,
                useToken: true,
                body,
              }),
            list: async (marketplaceId: number) =>
              get<Conversation[]>({
                url: `/marketplaces/${marketplaceId}/conversations/`,
                useToken: true,
              }),
          },
          message: {
            create: async (conversationId: number, body: object) =>
              post<Message>({
                url: `/conversations/${conversationId}/messages/`,
                useToken: true,
                body,
              }),
            get: async (conversationId: number) =>
              get<PaginatedMessageResponse>({
                url: `/conversations/${conversationId}/messages/`,
                useToken: true,
              }),
            read: async (conversationId: number, messageId: number) =>
              put({
                url: `/conversations/${conversationId}/read_to_message/${messageId}/`,
                useToken: true,
              }),
          },
        },
        marketplaces: {
          admin: async (marketplaceId?: number) =>
            get<MarketplaceAdminResponse | null>({
              url:
                `/marketplaces/my-admin/` +
                (marketplaceId ? `?marketplace_id=${marketplaceId}` : ''),
              useToken: true,
            }),
        },
      },
      users: {
        account: {
          admin: {
            create: async (marketplaceId: number, formData: FormData) =>
              post<string>({
                url: `/marketplaces/${marketplaceId}/create_admin/`,
                useToken: true,
                formData,
              }),
          },
          roles: {
            get: async (userId: number | string, roleId: number) =>
              get<RoleAccount>({
                url: `/users/${userId}/roles/${roleId}/`,
                useToken: true,
              }),
            createCheckoutSession: async (
              userId: number | string,
              roleId: number,
              body: { return_url: string; client_reference_id: string; preauth?: boolean },
            ) =>
              post<string>({
                url: `/users/${userId}/roles/${roleId}/checkout_session/`,
                useToken: true,
                body,
              }),
          },
        },
        checkEmail: async (email: string) =>
          performUnauthRequest<{ exists: boolean }>({
            method: 'POST',
            url: `/auth/email_exists/`,
            body: { email },
          }),
      },
      checkoutSession: {
        get: async (marketplaceId: number, sessionId: string) =>
          get<any>({
            url: `/checkout_session/${marketplaceId}/${sessionId}/`,
            useToken: true,
          }),
      },
      stripe: {
        account: {
          get: async (marketplaceId: number) =>
            get<StripeAccount>({
              url: `/stripe/${marketplaceId}/account/`,
              useToken: true,
            }),
        },
        accountSession: {
          post: async (marketplaceId: number, body: StripeConnectComponents) =>
            post<string>({
              url: `/stripe/${marketplaceId}/account_session/`,
              useToken: true,
              body,
            }),
        },
        settings: {
          post: async (marketplaceId: number, formData: FormData) =>
            post<StripeAccount>({
              url: `/stripe/${marketplaceId}/settings/`,
              useToken: true,
              formData,
            }),
        },
        oauth: {
          get: async (hostname: string, code: string) =>
            get<string>({
              url: `/stripe/code/${hostname}/${code}/`,
              useToken: false,
            }),
        },
      },
      attributes: {
        options: {
          put: async (
            marketplaceId: number,
            attributeId: number,
            optionId: number,
            body: AttributeOption,
          ) =>
            put<AttributeOption>({
              url: `/attributes/${marketplaceId}/${attributeId}/options/${optionId}/`,
              useToken: true,
              body,
            }),
        },
        get: async (attributeId: number) =>
          get<Attribute>({
            url: `/attributes/${attributeId}/`,
            useToken: false,
          }),
        createValue: async (marketplaceId: number, attributeId: number, body: object) =>
          post<AttributeOption>({
            url: `/attributes/${marketplaceId}/${attributeId}/values/create/`,
            useToken: true,
            body,
          }),
        checkNameExists: async (
          marketplaceId: number,
          categoryId: number,
          body: { name: string; id?: number },
        ) =>
          post<{ exists: boolean }>({
            url: `/categories/${marketplaceId}/${categoryId}/attributes/check_name/`,
            useToken: true,
            body,
          }),
      },
      listings: {
        admin_set_status: async (listingId: number, marketplaceId: number, body: object) =>
          put<Listing>({
            url: `/listings/${listingId}/${marketplaceId}/admin_set_status/`,
            useToken: true,
            body,
          }),
        loma_import: {
          post: async (formData: FormData) =>
            post<number[]>({
              url: `/listings/loma_import/`,
              useToken: true,
              formData,
            }),
        },
        enrich: {
          post: async (formData: FormData) =>
            post<number[]>({
              url: `/listings/enrich_listings/`,
              useToken: true,
              formData,
            }),
        },
      },
      digestEmailTypes: {
        list: async () => get<DigestEmailType[]>({ url: `/digest_email_types/`, useToken: true }),
        namespaceMapping: async (marketplaceId: number) =>
          get<TemplateNamespaceMapping>({
            url: `/marketplaces/${marketplaceId}/digest_email_types/namespace_mapping/`,
            useToken: true,
          }),
      },
      quickLaunch: {
        monetizationOptions: async (body: object) =>
          performUnauthRequest<MonetizationOption[]>({
            method: 'POST',
            url: `/quick_launch/monetization_options/`,
            body,
          }),
      },
      staff: {
        category: {
          getDetails: async (categoryId: number) =>
            get<Category>({
              url: `/staff/category/${categoryId}/`,
              useToken: true,
            }),
        },
        categories: {
          list: async (marketplaceId: number) =>
            get<BasicCategory[]>({
              url: `/staff/${marketplaceId}/all_categories/`,
              useToken: true,
            }),
        },
      },
      marketingOnboarding: {
        optOutAffiliate: {
          post: async () =>
            post<any>({
              url: '/marketing-onboarding/opt-out-affiliate/',
              useToken: true,
            }),
        },
        checklist: {
          get: async () =>
            get<OnboardingChecklist>({
              url: `/marketing-onboarding/checklist/`,
              useToken: true,
            }),
          post: async (field: string, value: boolean) =>
            post<OnboardingChecklist>({
              url: `/marketing-onboarding/checklist/`,
              useToken: true,
              body: { field, value },
            }),
        },
        launch: {
          post: async (marketplaceId: number) =>
            post<{ error?: string }>({
              url: `/marketing-onboarding/launch/`,
              useToken: true,
              body: { marketplace_id: marketplaceId },
            }),
        },
      },
      auth: {
        resendVerificationEmail: async () =>
          post<{ message: string }>({
            url: `/auth/resend_verification_email/`,
            useToken: true,
          }),
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [get, post, put, deleteRequest, remove, performUnauthRequest, putWithUserAccessToken],
  );

  const authGet = useMemo(
    () =>
      <T>(params: RequestUrlParams) =>
        performTokenedRequest<T>({ method: 'GET', ...params }),
    [performTokenedRequest],
  );

  const patch = useMemo(
    () =>
      <T>(params: RequestUrlParams) =>
        performTokenedRequest<T>({ method: 'PATCH', ...params }),
    [performTokenedRequest],
  );

  const unauthPost = useMemo(
    () =>
      <T>(params: RequestUrlParams) =>
        performUnauthRequest<T>({ method: 'POST', ...params }),
    [performUnauthRequest],
  );

  return {
    get,
    authGet,
    post,
    patch,
    put,
    delete: deleteRequest,
    api,
    unauthPost,
  };
};

export default useRequest;
